import React from 'react'

//UI

import { DatePicker, DummyInput, FileInput, Input, NumberInput, PriceInput, Select } from '@/components/Form'

//Hooks

//Hooks

import { FileType } from '@/features/files/constants/FileType'
import { Currency } from '@/features/vendors/constants'
import { isNullish } from '@/utils/helper'
import { Divider, Flex, SimpleGrid, Text } from '@chakra-ui/react'
import { CostBudgetType, SourcingPostType } from '@eapproval/offer-shared'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useWatch } from 'react-hook-form'

const VendorUploadQuotationInfoSection = ({ canUpload, requestType, costBudgetType }) => {
  const { t, i18n } = useTranslation()

  const requestRemark = useWatch({ name: 'requestRemark' })
  const isDetailedTableRequest = requestType === SourcingPostType.REQUEST_FOR_DETAILED_TABLE
  const effectiveCanUpload = canUpload && !isDetailedTableRequest
  return (
    <Flex direction={'column'} gap="2">
      {canUpload && (
        <>
          <Flex mb="2" gap="3">
            <Text w="15%" fontWeight={'bold'} fontSize={'large'}>
              {t('VENDOR_UPLOAD_PAGE_REQUEST_REMARK')}:{' '}
            </Text>
            <Text color={'teal'} fontWeight={'bold'} fontSize={'large'}>
              {requestRemark}
            </Text>
          </Flex>
          <Divider></Divider>
        </>
      )}

      <SimpleGrid columns={3} spacing={2}>
        {[
          SourcingPostType.REQUEST_FOR_QUOTATION,
          SourcingPostType.REQUEST_FOR_CLARIFICATION,
          SourcingPostType.REQUEST_FOR_DETAILED_TABLE,
        ].includes(requestType) && (
          <>
            <Select
              name="currency"
              label={t('VENDOR_UPLOAD_PAGE_CURRENCY')}
              options={Object.values(Currency).map((currency) => ({
                value: currency,
                label: currency,
              }))}
              isRow
              disabled={!effectiveCanUpload}
            ></Select>
            <PriceInput
              label={t('VENDOR_UPLOAD_PAGE_DATA_TABLE_DISCOUNTED_AMOUNT')}
              name={'amount'}
              isRow
              disabled={!effectiveCanUpload}
            />
          </>
        )}

        {[
          SourcingPostType.REQUEST_FOR_QUOTATION,
          SourcingPostType.REQUEST_FOR_CLARIFICATION,
          SourcingPostType.REQUEST_FOR_DETAILED_TABLE,
        ].includes(requestType) && (
          <FileInput
            label={t('VENDOR_UPLOAD_PAGE_FILES')}
            name="quotationFiles"
            fileType={FileType.VENDOR_QUOTATION}
            isRow
            disabled={!effectiveCanUpload || requestType === SourcingPostType.REQUEST_FOR_TECHNICAL_SUPPORT_DOCUMENT}
            isMultiple
            canRemove
            canUploadContinuously
            isRequired={false}
          />
        )}
        {[
          SourcingPostType.REQUEST_FOR_QUOTATION,
          SourcingPostType.REQUEST_FOR_CLARIFICATION,
          SourcingPostType.REQUEST_FOR_TECHNICAL_SUPPORT_DOCUMENT,
          SourcingPostType.REQUEST_FOR_DETAILED_TABLE,
        ].includes(requestType) && (
          <FileInput
            label={t('VENDOR_UPLOAD_PAGE_TECHNICAL_SUPPORT_DOCUMENTS')}
            name="technicalSupportDocuments"
            fileType={FileType.VENDOR_QUOTATION}
            isRow
            disabled={
              !effectiveCanUpload ||
              ![
                SourcingPostType.REQUEST_FOR_QUOTATION,
                SourcingPostType.REQUEST_FOR_TECHNICAL_SUPPORT_DOCUMENT,
                SourcingPostType.REQUEST_FOR_CLARIFICATION,
              ].includes(requestType)
            }
            isMultiple
            isRequired={requestType === SourcingPostType.REQUEST_FOR_TECHNICAL_SUPPORT_DOCUMENT}
            canRemove
            canUploadContinuously
          />
        )}

        {[
          SourcingPostType.REQUEST_FOR_QUOTATION,
          SourcingPostType.REQUEST_FOR_CLARIFICATION,
          SourcingPostType.REQUEST_FOR_UPDATE_OF_TERMS,
          SourcingPostType.REQUEST_FOR_DETAILED_TABLE,
        ].includes(requestType) && (
          <>
            <DatePicker
              name={'validDate'}
              minDate={moment().add(7, 'days').toDate()}
              isRow
              label={t('VENDOR_UPLOAD_PAGE_VALID_DATE')}
              disabled={!effectiveCanUpload}
            />
            <NumberInput
              label={t('VENDOR_UPLOAD_PAGE_CREDIT_TERMS')}
              name="creditTerm"
              isRow
              allowNegative={false}
              disabled={!effectiveCanUpload}
            />
            <Input
              label={t('VENDOR_UPLOAD_PAGE_PAYMENT_TERMS')}
              name="paymentTerm"
              isRow
              disabled={!effectiveCanUpload}
            />
            {costBudgetType === CostBudgetType.SUPPLY_MATERIAL_ONLY && (
              <>
                <Input
                  label={t('VENDOR_UPLOAD_PAGE_WARRANTY')}
                  name="warranty"
                  isRow
                  disabled={!effectiveCanUpload}
                  isRequired
                />
                <Input
                  label={t('VENDOR_UPLOAD_PAGE_INCOTERM')}
                  name="incoterm"
                  isRow
                  disabled={!effectiveCanUpload}
                  isRequired
                />
                <Input
                  label={t('VENDOR_UPLOAD_PAGE_LEAD_TIME')}
                  name="leadTime"
                  isRow
                  disabled={!effectiveCanUpload}
                  isRequired
                />
              </>
            )}
            {[CostBudgetType.SUPPLY_SITE_WORK, CostBudgetType.SUPPLY_SITE_WORK_RFQ].includes(costBudgetType) && (
              <>
                <Input
                  label={t('VENDOR_UPLOAD_PAGE_INCOTERM')}
                  name="incoterm"
                  isRequired={false}
                  isRow
                  disabled={!effectiveCanUpload}
                />
                <Input
                  label={t('VENDOR_UPLOAD_PAGE_DLP')}
                  name="DLP"
                  isRequired={true}
                  isRow
                  disabled={!effectiveCanUpload}
                />
                <Input
                  label={t('VENDOR_UPLOAD_PAGE_LEAD_TIME')}
                  name="leadTime"
                  isRequired={false}
                  isRow
                  disabled={!effectiveCanUpload}
                />
                <Input
                  label={t('VENDOR_UPLOAD_PAGE_INSURANCE')}
                  name="insurance"
                  isRow
                  disabled={!effectiveCanUpload}
                />
              </>
            )}

            {(costBudgetType === CostBudgetType.SUPPLY_ENGINEERING_SERVICE ||
              costBudgetType === CostBudgetType.SUPPLY_MAINTENANCE_SERVICE) && (
              <Input
                label={t('VENDOR_UPLOAD_PAGE_INSURANCE')}
                name="insurance"
                isRequired
                isRow
                disabled={!effectiveCanUpload}
              />
            )}
          </>
        )}
      </SimpleGrid>

      <Input
        name={`vendorRemark`}
        label={t('VENDOR_UPLOAD_PAGE_REMARKS')}
        disabled={!effectiveCanUpload}
        isRequired={false}
        labelWidth={10}
        isRow
      />
    </Flex>
  )
}

export default VendorUploadQuotationInfoSection
